// src/Pages/Paciente/CompletarPareja.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { db } from "../../firebase/config";
import {
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  collection,
  getDocs
} from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";

export default function CompletarPareja() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // const location = useLocation(); // No se estaba usando

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Formulario
  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    rut: "",
    email: "",
    telefono: "+569",
  });

  // ================================
  // 1) Obtener planId desde URL
  // ================================
  const { planId } = useParams();

  // ================================
  // 2) Cargar plan desde Firestore
  // ================================
  useEffect(() => {
    const cargar = async () => {
      try {
        if (!user) return;

        if (!planId) {
          setError("No se encontró el plan.");
          setLoading(false);
          return;
        }

        const ref = doc(db, "planes_usuarios", planId);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setError("El plan no existe.");
          setLoading(false);
          return;
        }

        const data = snap.data();

        // Validar que sea plan de pareja
        if (!data.es_pareja) {
          setError("Este plan no corresponde a entrenamiento en pareja.");
          setLoading(false);
          return;
        }

        // Validar titular (seguridad extra en frontend)
        if (data.titular_uid !== user.uid) {
          setError("Solo el titular del plan puede completar la información.");
          setLoading(false);
          return;
        }

        // Validar si ya está completo
        if (data.pareja_completa) {
          setError("Este plan ya está completado.");
          setTimeout(() => navigate("/paciente/sesiones"), 2000);
          return;
        }

        // Si todo ok, dejamos de cargar (no necesitamos guardar 'plan' en estado si no lo usamos para renderizar nada específico aparte de validaciones)
        setLoading(false);

      } catch (err) {
        console.error(err);
        setError("Error al cargar el plan.");
        setLoading(false);
      }
    };

    cargar();
  }, [user, planId, navigate]);

    // ================================
    // FORMATEAR RUT
    // ================================
    const formatearRut = (valor) => {
      // Elimina todo excepto números y K/k
      let limpio = valor.replace(/[^0-9kK]/g, "").toUpperCase();

      if (limpio.length <= 1) return limpio;

      const cuerpo = limpio.slice(0, -1);
      const dv = limpio.slice(-1);

      return `${cuerpo}-${dv}`;
    };

    const updateForm = (e) => {
      const { name, value } = e.target;

      let nuevoValor = value;

      if (name === "rut") {
        nuevoValor = formatearRut(value);
      }

      if (name === "telefono") {
        // Mantener siempre el prefijo +569
        const numeros = value.replace(/\D/g, "");

        if (numeros.length <= 3) {
          nuevoValor = "+569";
        } else {
          nuevoValor = "+569" + numeros.slice(3);
        }
      }

      setForm((f) => ({
        ...f,
        [name]: nuevoValor,
      }));
    };



  // ================================
  // VALIDADOR DE RUT SIMPLE
  // ================================
  const validarRUT = (rut) => {
    // Validación básica de formato
    return /^[0-9]+[-|‐]{1}[0-9kK]{1}$/.test(rut);
  };

  // ================================
  // HANDLER SUBMIT
  // ================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.nombre || !form.apellido || !form.email || !form.telefono) {
      setError("Completa todos los campos obligatorios.");
      return;
    }

    if (!validarRUT(form.rut)) {
      setError("El RUT no tiene un formato válido. Ej: 12345678-9");
      return;
    }

    try {
      setLoading(true);

      // Buscar si ese email corresponde a un usuario real
      let acompananteUid = null;

      const q = query(
        collection(db, "usuarios"),
        where("email", "==", form.email.toLowerCase())
      );

      const qs = await getDocs(q);

      if (!qs.empty) {
        acompananteUid = qs.docs[0].id;  // ← UID real del acompañante
      }


      // --------------------------------------------
      // Actualizar el plan
      // --------------------------------------------
      const ref = doc(db, "planes_usuarios", planId);

      await updateDoc(ref, {
        acompanante_uid: acompananteUid,
        acompanante_info: {
          nombre: form.nombre.trim(),
          apellido: form.apellido.trim(),
          rut: form.rut.trim(),
          email: form.email.toLowerCase(),
          telefono: form.telefono,
        },
        pareja_completa: true,
        updatedAt: new Date(),
      });

      // ✔ Redirigir a sesiones
      navigate("/paciente/sesiones");

    } catch (err) {
      console.error("Error al guardar:", err);
      // Muestra el mensaje de error real si es posible, o uno genérico
      setError("Error al guardar la información. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  // ================================
  // UI
  // ================================
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Cargando...
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600 font-medium">
        {error}
      </div>
    );

  return (
    <div className="max-w-lg mx-auto bg-white shadow-md rounded-xl p-6 mt-10">
      <h1 className="text-2xl font-bold mb-4 text-gray-800">
        Completar datos del acompañante
      </h1>

      <p className="text-gray-600 mb-6">
        Este formulario es obligatorio para activar el plan de entrenamiento en pareja.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre *
          </label>
          <input
            type="text"
            name="nombre"
            value={form.nombre}
            onChange={updateForm}
            className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Apellido *
          </label>
          <input
            type="text"
            name="apellido"
            value={form.apellido}
            onChange={updateForm}
            className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            RUT *
          </label>
          <input
            type="text"
            name="rut"
            placeholder="12345678-9"
            value={form.rut}
            onChange={updateForm}
            className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email del acompañante *
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={updateForm}
            className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Si el acompañante tiene cuenta en EKINE, se vinculará internamente más adelante.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Teléfono *
          </label>
          <input
            type="text"
            name="telefono"
            value={form.telefono}
            onChange={updateForm}
            className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none"
            required
          />
        </div>

        {error && (
          <p className="text-red-600 text-sm font-medium bg-red-50 p-2 rounded border border-red-200">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className={`w-full text-white p-3 rounded-lg font-semibold transition ${
            loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {loading ? "Guardando..." : "Guardar y continuar"}
        </button>
      </form>
    </div>
  );
}